import { useState, useEffect, useMemo } from "react";
import SiderBar from "../../components/siderbarcomponents";
import {
  subscribeToEmpleados,
  subscribeToServiciosPersonal,
  subscribeToPagosNominaPersonal,
  addPagoNominaPersonal,
  empleadoEnServicio,
  Empleado,
  Servicio,
  PagoNominaPersonal,
} from "../../firebase/firebaseService";
import {
  FaFileInvoiceDollar,
  FaCheckCircle,
  FaExclamationTriangle,
  FaPrint,
  FaTimes,
  FaChevronLeft,
  FaChevronRight,
  FaClock,
  FaDollarSign,
  FaHome,
  FaUser,
} from "react-icons/fa";

// ─── Helpers ───────────────────────────────────────────────────────────────
const MESES = ["Enero","Febrero","Marzo","Abril","Mayo","Junio","Julio","Agosto","Septiembre","Octubre","Noviembre","Diciembre"];

const getQuincenaKey = (date: Date): string => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const q = date.getDate() <= 15 ? "1" : "2";
  return `${y}-${m}-${q}`;
};

const getQuincenaLabel = (key: string): string => {
  const [y, m, q] = key.split("-");
  const mes = MESES[parseInt(m) - 1];
  return q === "1" ? `1–15 de ${mes} ${y}` : `16–31 de ${mes} ${y}`;
};

const getQuincenaEndDate = (key: string): Date => {
  const [y, m, q] = key.split("-").map(Number);
  if (q === 1) return new Date(y, m - 1, 15, 23, 59, 59);
  return new Date(y, m, 0, 23, 59, 59);
};

const getPastQuincenas = (n: number): string[] => {
  const list: string[] = [];
  let y = new Date().getFullYear();
  let mo = new Date().getMonth() + 1;
  let q = new Date().getDate() <= 15 ? 1 : 2;
  for (let i = 0; i < n; i++) {
    if (q === 1) { q = 2; mo--; if (mo === 0) { mo = 12; y--; } }
    else q = 1;
    list.push(`${y}-${String(mo).padStart(2,"0")}-${q}`);
  }
  return list;
};

const formatDate = (d: Date) =>
  d.toLocaleDateString("en-US", { month: "2-digit", day: "2-digit", year: "numeric" });

const formatTime = (d: Date) =>
  d.toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit" });

// ─── PDF Generator ─────────────────────────────────────────────────────────
const generarPDF = (
  empleado: Empleado,
  servicios: Servicio[],
  quincena: string,
  totalPagado: number,
  deudasAnteriores: { quincena: string; monto: number }[]
) => {
  const win = window.open("", "_blank");
  if (!win) return;

  const today = new Date().toLocaleDateString("es-ES", { day: "2-digit", month: "long", year: "numeric" });
  const totalHoras = servicios.reduce((s, sv) => s + sv.horasTrabajadas, 0);

  const rowsHtml = servicios.map(sv => `
    <tr>
      <td>${formatDate(sv.fechaServicio)}</td>
      <td>${sv.casaNombre}</td>
      <td style="text-align:center">${formatTime(sv.fechaHoraInicio)} – ${formatTime(sv.fechaHoraFin)}</td>
      <td style="text-align:center">${sv.horasTrabajadas}h</td>
      <td style="text-align:center">$${empleado.costoPorHora.toFixed(2)}</td>
      <td style="text-align:right;font-weight:600">$${(sv.horasTrabajadas * empleado.costoPorHora).toFixed(2)}</td>
    </tr>
  `).join("");

  const deudasHtml = deudasAnteriores.length > 0
    ? `<div class="aviso-deuda">
        <strong>⚠ Deuda de quincenas anteriores:</strong>
        ${deudasAnteriores.map(d => `<span>${getQuincenaLabel(d.quincena)}: <strong>$${d.monto.toFixed(2)}</strong></span>`).join(" &nbsp;|&nbsp; ")}
        <br/>Total acumulado: <strong>$${(totalPagado + deudasAnteriores.reduce((s,d)=>s+d.monto,0)).toFixed(2)}</strong>
       </div>`
    : "";

  const html = `<!DOCTYPE html>
<html lang="es">
<head>
  <meta charset="UTF-8">
  <title>Comprobante de Pago Personal – ${empleado.nombre} ${empleado.apellidos}</title>
  <style>
    @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800&display=swap');
    *{margin:0;padding:0;box-sizing:border-box}
    body{font-family:'Inter',sans-serif;background:#fff;color:#1f2937;padding:40px}
    .header{display:flex;justify-content:space-between;align-items:flex-start;padding-bottom:24px;border-bottom:3px solid #ec4899;margin-bottom:32px}
    .brand{font-size:26px;font-weight:800;color:#ec4899;letter-spacing:-0.5px}
    .brand-sub{font-size:12px;color:#6b7280;margin-top:3px}
    .tag-personal{background:#fce7f3;color:#be185d;font-size:10px;padding:3px 8px;border-radius:4px;font-weight:700;text-transform:uppercase;letter-spacing:.05em;margin-top:6px;display:inline-block}
    .receipt-label{text-align:right}
    .receipt-label h2{font-size:18px;font-weight:700;color:#374151}
    .receipt-label p{font-size:12px;color:#9ca3af;margin-top:3px}
    .grid{display:grid;grid-template-columns:1fr 1fr;gap:16px;margin-bottom:28px}
    .card{background:#fdf2f8;border:1px solid #fbcfe8;border-radius:10px;padding:14px}
    .card label{font-size:10px;color:#9ca3af;text-transform:uppercase;letter-spacing:.06em;display:block;margin-bottom:4px}
    .card span{font-size:14px;font-weight:700;color:#1f2937}
    table{width:100%;border-collapse:collapse;margin-bottom:24px;font-size:13px}
    th{background:#ec4899;color:#fff;padding:10px 12px;text-align:left;font-weight:600;font-size:11px;text-transform:uppercase;letter-spacing:.05em}
    td{padding:9px 12px;border-bottom:1px solid #f3f4f6}
    tr:nth-child(even) td{background:#fdf2f8}
    .total-box{background:linear-gradient(135deg,#ec4899,#f472b6);color:#fff;border-radius:12px;padding:20px 24px;text-align:right;display:inline-block;float:right;min-width:240px;margin-bottom:32px}
    .total-box .label{font-size:11px;opacity:.8;text-transform:uppercase;letter-spacing:.08em}
    .total-box .amount{font-size:30px;font-weight:800;margin-top:4px}
    .clearfix::after{content:"";display:table;clear:both}
    .sigs{display:grid;grid-template-columns:1fr 1fr;gap:80px;margin-top:60px}
    .sig-line{border-top:2px solid #d1d5db;padding-top:8px;text-align:center;font-size:11px;color:#6b7280}
    .footer{margin-top:32px;text-align:center;font-size:11px;color:#9ca3af;border-top:1px solid #e5e7eb;padding-top:16px}
    .aviso-deuda{background:#fef3c7;border:1px solid #fcd34d;border-radius:8px;padding:12px 16px;margin-bottom:20px;font-size:12px;color:#92400e;line-height:1.8}
    @media print{body{padding:20px}.no-print{display:none!important}}
  </style>
</head>
<body>
  <div class="header">
    <div>
      <div class="brand">GOBIES CLEANING SERVICES</div>
      <div class="brand-sub">Servicios Profesionales de Limpieza</div>
      <div class="tag-personal">🏠 Persona Natural</div>
    </div>
    <div class="receipt-label">
      <h2>COMPROBANTE DE PAGO</h2>
      <p>Emitido: ${today}</p>
    </div>
  </div>

  <div class="grid">
    <div class="card"><label>Empleado</label><span>${empleado.nombre} ${empleado.apellidos}</span></div>
    <div class="card"><label>Período de Pago</label><span>${getQuincenaLabel(quincena)}</span></div>
    <div class="card"><label>Tarifa por Hora</label><span>$${empleado.costoPorHora.toFixed(2)} / hora</span></div>
    <div class="card"><label>Total Horas Trabajadas</label><span>${totalHoras.toFixed(2)} horas</span></div>
  </div>

  ${deudasHtml}

  <table>
    <thead>
      <tr>
        <th>Fecha</th>
        <th>Casa / Cliente</th>
        <th style="text-align:center">Horario</th>
        <th style="text-align:center">Horas</th>
        <th style="text-align:center">Tarifa</th>
        <th style="text-align:right">Subtotal</th>
      </tr>
    </thead>
    <tbody>${rowsHtml}</tbody>
  </table>

  <div class="clearfix">
    <div class="total-box">
      <div class="label">Total a Pagar (esta quincena)</div>
      <div class="amount">$${totalPagado.toFixed(2)}</div>
    </div>
  </div>

  <div class="sigs">
    <div class="sig-line">Firma del Empleador</div>
    <div class="sig-line">Firma del Empleado · ${empleado.nombre} ${empleado.apellidos}</div>
  </div>

  <div class="footer">
    Gobies Cleaning Services (Persona Natural) &nbsp;·&nbsp; Comprobante generado el ${today} &nbsp;·&nbsp; Este documento sirve como recibo oficial de pago
  </div>

  <script>setTimeout(function(){window.print();},600);</script>
</body>
</html>`;

  win.document.write(html);
  win.document.close();
};

// ─── Component ─────────────────────────────────────────────────────────────
export default function NominaPersonalAdmin() {
  const [empleados, setEmpleados] = useState<Empleado[]>([]);
  const [servicios, setServicios] = useState<Servicio[]>([]);
  const [pagos, setPagos] = useState<PagoNominaPersonal[]>([]);
  const [loading, setLoading] = useState(true);

  const currentQ = getQuincenaKey(new Date());
  const allTabs = [currentQ, ...getPastQuincenas(11)]; // 12 quincenas = 6 meses
  const [activeTab, setActiveTab] = useState(currentQ);

  const [selectedEmp, setSelectedEmp] = useState<Empleado | null>(null);
  const [paying, setPaying] = useState(false);

  useEffect(() => {
    const u1 = subscribeToEmpleados((d) => { setEmpleados(d); setLoading(false); });
    const u2 = subscribeToServiciosPersonal(setServicios);
    const u3 = subscribeToPagosNominaPersonal(setPagos);
    return () => { u1(); u2(); u3(); };
  }, []);

  // For each employee in a quincena, get their services and payment status
  const empStats = useMemo(() => {
    return empleados.map((emp) => {
      const empServicios = servicios.filter(
        (s) => s.quincena === activeTab &&
          (empleadoEnServicio(s, emp.id!))
      );
      const totalHoras = empServicios.reduce((s, sv) => s + sv.horasTrabajadas, 0);
      const totalDeuda = totalHoras * (emp.costoPorHora || 0);

      const pagoExistente = pagos.find(
        (p) => p.empleadoId === emp.id && p.quincena === activeTab
      );
      const pagado = !!pagoExistente;

      // Check overdue: quincena ended and not paid
      const endDate = getQuincenaEndDate(activeTab);
      const overdue = !pagado && endDate < new Date() && empServicios.length > 0;

      // Unpaid previous quincenas
      const deudasAnteriores = getPastQuincenas(6)
        .map((q) => {
          const svs = servicios.filter(
            (s) => s.quincena === q &&
              (empleadoEnServicio(s, emp.id!))
          );
          const pago = pagos.find((p) => p.empleadoId === emp.id && p.quincena === q);
          if (!pago && svs.length > 0) {
            const hrs = svs.reduce((s, sv) => s + sv.horasTrabajadas, 0);
            return { quincena: q, monto: hrs * (emp.costoPorHora || 0) };
          }
          return null;
        })
        .filter(Boolean) as { quincena: string; monto: number }[];

      return { emp, empServicios, totalHoras, totalDeuda, pagado, overdue, pagoExistente, deudasAnteriores };
    }).filter((d) => d.empServicios.length > 0 || d.pagado);
  }, [empleados, servicios, pagos, activeTab]);

  const handlePagar = async () => {
    if (!selectedEmp) return;
    const stat = empStats.find((s) => s.emp.id === selectedEmp.id);
    if (!stat) return;
    setPaying(true);
    try {
      await addPagoNominaPersonal({
        empleadoId: selectedEmp.id!,
        empleadoNombre: `${selectedEmp.nombre} ${selectedEmp.apellidos}`,
        quincena: activeTab,
        totalHoras: stat.totalHoras,
        costoPorHora: selectedEmp.costoPorHora,
        totalPagado: stat.totalDeuda,
        serviciosIds: stat.empServicios.map((s) => s.id!),
        fechaPago: new Date(),
      });
      generarPDF(selectedEmp, stat.empServicios, activeTab, stat.totalDeuda, stat.deudasAnteriores);
      setSelectedEmp(null);
    } catch (err) {
      console.error(err);
    } finally {
      setPaying(false);
    }
  };

  // Total deuda global (all unpaid for selected quincena)
  const totalDeudaTab = empStats.filter((s) => !s.pagado).reduce((s, d) => s + d.totalDeuda, 0);
  const totalPagadoTab = empStats.filter((s) => s.pagado).reduce((s, d) => s + d.totalDeuda, 0);

  const selectedStat = selectedEmp ? empStats.find((s) => s.emp.id === selectedEmp.id) : null;

  return (
    <div className="flex-col lg:flex-row flex min-h-screen bg-slate-50">
      <SiderBar />

      <div className="flex-1 overflow-auto">
        {/* Header */}
        <div className="bg-gradient-to-r from-rose-500 to-pink-500 px-8 py-8">
          <div className="flex items-center gap-3 mb-1">
            <div className="w-7 h-7 bg-white/20 rounded-lg flex items-center justify-center">
              <FaUser className="text-white" size={13}/>
            </div>
            <span className="text-white/80 text-sm font-semibold uppercase tracking-widest">Persona Natural</span>
          </div>
          <h1 className="text-3xl font-bold text-white">Nómina Personal</h1>
          <p className="text-rose-100 text-sm mt-1">Control de pagos a empleados · servicios independientes</p>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-6">
            <div className="bg-white/20 backdrop-blur rounded-2xl p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-white/30 rounded-xl flex items-center justify-center">
                  <FaDollarSign className="text-white" size={18} />
                </div>
                <div>
                  <p className="text-rose-100 text-[13px] font-medium">Deuda pendiente</p>
                  <p className="text-white text-2xl font-bold">${totalDeudaTab.toFixed(0)}</p>
                </div>
              </div>
            </div>
            <div className="bg-white/20 backdrop-blur rounded-2xl p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-white/30 rounded-xl flex items-center justify-center">
                  <FaCheckCircle className="text-white" size={18} />
                </div>
                <div>
                  <p className="text-rose-100 text-[13px] font-medium">Total pagado</p>
                  <p className="text-white text-2xl font-bold">${totalPagadoTab.toFixed(0)}</p>
                </div>
              </div>
            </div>
            <div className="bg-white/20 backdrop-blur rounded-2xl p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-white/30 rounded-xl flex items-center justify-center">
                  <FaExclamationTriangle className="text-white" size={18} />
                </div>
                <div>
                  <p className="text-rose-100 text-[13px] font-medium">Pagos urgentes</p>
                  <p className="text-white text-2xl font-bold">{empStats.filter((s) => s.overdue).length}</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="p-6 lg:p-8">
          {/* Quincena tabs */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="flex overflow-x-auto border-b border-gray-100 bg-gray-50">
              {allTabs.map((tab, idx) => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`px-5 py-3.5 text-sm font-semibold whitespace-nowrap transition-colors ${
                    activeTab === tab
                      ? "bg-white text-rose-600 border-b-2 border-rose-500"
                      : "text-gray-500 hover:text-gray-700 hover:bg-gray-100"
                  }`}
                >
                  {idx === 0 ? "Quincena Actual" : getQuincenaLabel(tab)}
                </button>
              ))}
            </div>

            {loading ? (
              <div className="flex items-center justify-center h-48">
                <div className="animate-spin w-8 h-8 border-4 border-rose-300 border-t-rose-600 rounded-full" />
              </div>
            ) : empStats.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-48 text-gray-400">
                <FaFileInvoiceDollar size={40} className="mb-3 text-gray-200" />
                <p className="font-medium">Sin servicios en esta quincena</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-100">
                      <th className="text-left px-6 py-4 text-xs font-semibold text-gray-500 uppercase">Empleado</th>
                      <th className="text-left px-6 py-4 text-xs font-semibold text-gray-500 uppercase hidden sm:table-cell">Servicios</th>
                      <th className="text-left px-6 py-4 text-xs font-semibold text-gray-500 uppercase">Horas</th>
                      <th className="text-left px-6 py-4 text-xs font-semibold text-gray-500 uppercase">Monto</th>
                      <th className="text-left px-6 py-4 text-xs font-semibold text-gray-500 uppercase">Estado</th>
                      <th className="px-6 py-4" />
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {empStats.map(({ emp, empServicios, totalHoras, totalDeuda, pagado, overdue, deudasAnteriores }) => (
                      <tr
                        key={emp.id}
                        className={`transition-colors ${
                          overdue
                            ? "bg-red-50 hover:bg-red-100"
                            : "hover:bg-rose-50/30"
                        }`}
                      >
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-3">
                            <div className={`w-9 h-9 rounded-full flex items-center justify-center text-white text-xs font-bold shrink-0 ${
                              overdue ? "bg-red-500" : "bg-gradient-to-br from-rose-400 to-pink-500"
                            }`}>
                              {emp.nombre[0]}{emp.apellidos[0]}
                            </div>
                            <div>
                              <p className={`font-semibold text-sm ${overdue ? "text-red-700" : "text-gray-800"}`}>
                                {emp.nombre} {emp.apellidos}
                              </p>
                              {overdue && (
                                <p className="text-[11px] text-red-500 font-bold flex items-center gap-1">
                                  <FaExclamationTriangle size={9} /> PAGAR URGENTE
                                </p>
                              )}
                              {deudasAnteriores.length > 0 && !overdue && (
                                <p className="text-[11px] text-rose-500 font-semibold">
                                  Deuda anterior: ${deudasAnteriores.reduce((s,d)=>s+d.monto,0).toFixed(0)}
                                </p>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-600 hidden sm:table-cell">
                          {empServicios.length} servicio{empServicios.length !== 1 ? "s" : ""}
                        </td>
                        <td className="px-6 py-4">
                          <span className="flex items-center gap-1 text-sm font-semibold text-gray-700">
                            <FaClock size={11} className="text-gray-400" />{totalHoras.toFixed(1)}h
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <span className={`font-bold text-sm ${overdue ? "text-red-600" : "text-gray-800"}`}>
                            ${totalDeuda.toFixed(2)}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          {pagado ? (
                            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-emerald-100 text-emerald-700">
                              <FaCheckCircle size={10} /> PAGADO
                            </span>
                          ) : overdue ? (
                            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-red-100 text-red-700 animate-pulse">
                              <FaExclamationTriangle size={10} /> URGENTE
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-rose-100 text-rose-700">
                              PENDIENTE
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-4 text-right">
                          <button
                            onClick={() => setSelectedEmp(emp)}
                            className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-all ${
                              pagado
                                ? "bg-gray-100 text-gray-500 hover:bg-gray-200"
                                : "bg-rose-500 text-white hover:bg-rose-600 shadow-md shadow-rose-200"
                            }`}
                          >
                            {pagado ? "Ver detalle" : "Ver y Pagar"}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Modal: Detalle empleado */}
      {selectedEmp && selectedStat && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            {/* Modal header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-100">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-rose-400 to-pink-500 flex items-center justify-center text-white font-bold">
                  {selectedEmp.nombre[0]}{selectedEmp.apellidos[0]}
                </div>
                <div>
                  <h2 className="text-lg font-bold text-gray-800">{selectedEmp.nombre} {selectedEmp.apellidos}</h2>
                  <p className="text-sm text-gray-400">{getQuincenaLabel(activeTab)}</p>
                </div>
              </div>
              <button onClick={() => setSelectedEmp(null)} className="p-2 rounded-lg hover:bg-gray-100 text-gray-500">
                <FaTimes size={16} />
              </button>
            </div>

            {/* Info bar */}
            <div className="grid grid-cols-3 gap-3 p-6 border-b border-gray-100 bg-gray-50">
              <div className="text-center">
                <p className="text-[13px] text-gray-400 font-medium">Servicios</p>
                <p className="text-xl font-bold text-gray-800">{selectedStat.empServicios.length}</p>
              </div>
              <div className="text-center">
                <p className="text-[13px] text-gray-400 font-medium">Horas totales</p>
                <p className="text-xl font-bold text-gray-800">{selectedStat.totalHoras.toFixed(1)}h</p>
              </div>
              <div className="text-center">
                <p className="text-[13px] text-gray-400 font-medium">Tarifa/hora</p>
                <p className="text-xl font-bold text-gray-800">${selectedEmp.costoPorHora.toFixed(2)}</p>
              </div>
            </div>

            {/* Alertas deudas anteriores */}
            {selectedStat.deudasAnteriores.length > 0 && (
              <div className="mx-6 mt-4 p-4 bg-rose-50 border border-rose-200 rounded-xl">
                <p className="text-xs font-bold text-rose-700 mb-2 flex items-center gap-1.5">
                  <FaExclamationTriangle size={12} /> Deudas de quincenas anteriores sin pagar:
                </p>
                {selectedStat.deudasAnteriores.map((d) => (
                  <div key={d.quincena} className="flex justify-between text-sm text-rose-700">
                    <span>{getQuincenaLabel(d.quincena)}</span>
                    <span className="font-bold">${d.monto.toFixed(2)}</span>
                  </div>
                ))}
              </div>
            )}

            {/* Services table */}
            <div className="p-6">
              <p className="text-sm font-semibold text-gray-600 mb-3">Servicios de la quincena:</p>
              {selectedStat.empServicios.length === 0 ? (
                <p className="text-gray-400 text-sm text-center py-6">Sin servicios registrados en esta quincena</p>
              ) : (
                <div className="overflow-x-auto rounded-xl border border-gray-100">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Fecha</th>
                        <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Casa</th>
                        <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase hidden sm:table-cell">Horario</th>
                        <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Horas</th>
                        <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Subtotal</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-50">
                      {selectedStat.empServicios.map((sv) => (
                        <tr key={sv.id} className="hover:bg-gray-50">
                          <td className="px-4 py-3 text-gray-600">{formatDate(sv.fechaServicio)}</td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-1.5 text-gray-700">
                              <FaHome size={11} className="text-gray-400" />{sv.casaNombre}
                            </div>
                          </td>
                          <td className="px-4 py-3 text-gray-500 hidden sm:table-cell">
                            {formatTime(sv.fechaHoraInicio)} – {formatTime(sv.fechaHoraFin)}
                          </td>
                          <td className="px-4 py-3 text-right font-semibold text-gray-700">{sv.horasTrabajadas}h</td>
                          <td className="px-4 py-3 text-right font-bold text-gray-800">
                            ${(sv.horasTrabajadas * selectedEmp.costoPorHora).toFixed(2)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Total */}
              <div className="mt-4 flex justify-end">
                <div className="bg-gradient-to-r from-rose-500 to-pink-500 text-white rounded-xl px-6 py-4 text-right min-w-[200px]">
                  <p className="text-xs opacity-80 uppercase tracking-wider">Total a Pagar</p>
                  <p className="text-3xl font-bold mt-1">${selectedStat.totalDeuda.toFixed(2)}</p>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex gap-3 p-6 border-t border-gray-100">
              <button
                onClick={() => setSelectedEmp(null)}
                className="flex-1 py-2.5 rounded-xl border border-gray-200 text-gray-600 font-semibold text-sm hover:bg-gray-50 transition-colors"
              >
                Cerrar
              </button>
              {!selectedStat.pagado && selectedStat.empServicios.length > 0 && (
                <button
                  onClick={handlePagar}
                  disabled={paying}
                  className="flex-1 py-2.5 rounded-xl bg-gradient-to-r from-rose-500 to-pink-500 text-white font-bold text-sm hover:from-rose-600 hover:to-pink-600 transition-all disabled:opacity-60 shadow-lg shadow-rose-200 flex items-center justify-center gap-2"
                >
                  <FaPrint size={14} />
                  {paying ? "Procesando..." : "Pagar y Generar PDF"}
                </button>
              )}
              {selectedStat.pagado && (
                <button
                  onClick={() => generarPDF(selectedEmp, selectedStat.empServicios, activeTab, selectedStat.totalDeuda, [])}
                  className="flex-1 py-2.5 rounded-xl bg-gray-100 text-gray-700 font-bold text-sm hover:bg-gray-200 transition-colors flex items-center justify-center gap-2"
                >
                  <FaPrint size={14} /> Reimprimir PDF
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
